import { lazy, Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Navigate, Route, Routes, useParams } from "react-router-dom";
import { Routes as RoutesList } from "routes";
import { useSelector } from "react-redux";
import { usePostHog } from "posthog-js/react";
import GenericErrorPage from "ui/GenericErrorPage";
import { PrivateFacilitiesRoute, PrivateRoute, PrivateVesselRoute } from "PrivateRoutes";
import { LoadingIndicator, userSelector, useFeatureFlag } from "@shape/reef-shared";
import type { StepProps, Step2Response as EpsStep2Response } from "@shape/reef-shared";
import type { Step2Response as ReefStep2Response } from "types";
import { NewStep2EvaluationRemaster } from "pages/cto/pages/NewCtoDetails/components/NewStep2Evaluation/NewStep2EvaluationRemaster";

const UserPermission = lazy(() => import("pages/userPermissions/UserPermission.old"));
const GroupPermissions = lazy(() => import("pages/groupPermissions/GroupPermissions"));
const TagsAndWOsPage = lazy(() => import("pages/tagsAndWorkOrders/TagsAndWOsPage"));
const CsgTagsAndWOsPage = TagsAndWOsPage;
const TagsPage = lazy(() => import("pages/tags"));
const TagsEpsPage = lazy(() => import("@shape/tags-eps"));
const ErrorPage = lazy(() => import("ui/ErrorPage"));
const WonosPage = lazy(() => import("pages/workOrders/WonosPage"));
const WonosEPSPage = lazy(() => import("@shape/wo-eps"));
const HomePage = lazy(() => import("pages/home"));
const MahsPage = lazy(() => import("pages/mah/MahsPage"));
const ScesPage = lazy(() => import("pages/sce/pages/ScesPage"));

const CtoDetails = lazy(() => import("pages/cto/pages/CtoDetails"));
const CtoList = lazy(() => import("pages/cto/pages/CtoList"));
const Dashboard = lazy(() => import("pages/cto/pages/Dashboard"));

const CtoDetailsEps = lazy(() => import("@shape/cto-eps").then((m) => ({ default: m.CtoDetails })));
const CtoListEps = lazy(() => import("@shape/cto-eps").then((m) => ({ default: m.CtoList })));
const DashboardEps = lazy(() => import("@shape/cto-eps").then((m) => ({ default: m.Dashboard })));

const GroupedCtoStep2 = (props: StepProps<EpsStep2Response>) => (
  <NewStep2EvaluationRemaster {...(props as unknown as StepProps<ReefStep2Response>)} />
);

const BowtiePage = lazy(() => import("pages/bowtie"));
const BowtiePageEPS = lazy(() => import("@shape/bowtie"));
const TagsPageForSce = lazy(() => import("pages/sce/pages/tagsPageForSce"));
const ScesByMah = lazy(() => import("pages/vessel/pages/ScesByMahs"));
const FacilitiesPage = lazy(() => import("pages/vessel/pages/FacilitiesPage"));
const FacilitiesEPSPage = lazy(() => import("@shape/facilities-eps"));
const Hc = lazy(() => import("@shape/hc"));
const Login = lazy(() => import("pages/login"));

type PageName =
  | "Facilities"
  | "DashBoard"
  | "CtoDetails"
  | "CtoList"
  | "Bowtie"
  | "Tags"
  | "Hc"
  | "Wo";

const pagesMap: Record<PageName, { core: JSX.Element; eps: JSX.Element }> = {
  Facilities: { core: <FacilitiesPage />, eps: <FacilitiesEPSPage /> },
  DashBoard: { core: <Dashboard />, eps: <DashboardEps /> },
  CtoDetails: { core: <CtoDetails />, eps: <CtoDetailsEps groupedStep2Form={GroupedCtoStep2} /> },
  CtoList: { core: <CtoList />, eps: <CtoListEps /> },
  Bowtie: { core: <BowtiePage />, eps: <BowtiePageEPS /> },
  Tags: { core: <TagsPage />, eps: <TagsEpsPage /> },
  Hc: { core: <ScesByMah />, eps: <Hc /> },
  Wo: { core: <WonosPage />, eps: <WonosEPSPage /> },
};

type EpsRedirectProps = {
  page: PageName;
};

function EpsRedirect({ page }: EpsRedirectProps) {
  const { country } = useParams();

  return country === "GY" ? pagesMap[page].eps : pagesMap[page].core;
}

export function VesselRedirect() {
  const { country } = useParams();

  if (!country) return null;

  return <Navigate to={RoutesList.FacilitiesPage.replace(":country", country)} replace />;
}

export function RoutesContainer() {
  const user = useSelector(userSelector);
  const postHog = usePostHog();
  const isMahsByScePageEnabled = useFeatureFlag("mahs-by-sce-page");

  useEffect(() => {
    if (user && postHog) {
      postHog.identify(user.mail ?? user.email ?? "", {
        email: user.mail ?? user.email ?? "",
        name: user.name,
        url: window.location.href,
        env: import.meta.env.VITE_ENVIRONMENT,
        app_version: __APP_VERSION__,
      });
    }
  }, [postHog, user]);

  return (
    <ErrorBoundary FallbackComponent={GenericErrorPage}>
      <Suspense fallback={<LoadingIndicator />}>
        <Routes>
          {/* REDIRECTS */}

          <Route path={RoutesList.VesselRedirect} element={<VesselRedirect />} />
          <Route
            path={RoutesList.BowtieRedirect}
            element={<Navigate to={RoutesList.BowtiePage} replace />}
          />
          {/* PAGES */}
          <Route
            path={RoutesList.UserPermissionsPage}
            element={<PrivateRoute element={<UserPermission />} validation={() => true} />}
          />
          <Route
            path={RoutesList.BowtiePage}
            element={<PrivateVesselRoute element={<EpsRedirect page="Bowtie" />} />}
          />
          <Route
            path={RoutesList.GroupPermissionsPage}
            element={<PrivateRoute element={<GroupPermissions />} validation={() => true} />}
          />
          <Route
            path={RoutesList.TagsAndWOsPage}
            element={<PrivateVesselRoute element={<TagsAndWOsPage />} />}
          />
          <Route
            path={RoutesList.CsgTagsAndWOsPage}
            element={<PrivateVesselRoute element={<CsgTagsAndWOsPage />} />}
          />
          <Route
            path={RoutesList.TagsPageForSce}
            element={<PrivateVesselRoute element={<TagsPageForSce />} />}
          />
          <Route
            path={RoutesList.ScesPage}
            element={<PrivateVesselRoute element={<ScesPage />} />}
          />
          <Route
            path={RoutesList.MahsPage}
            element={<PrivateVesselRoute element={<EpsRedirect page="Hc" />} />}
          />
          <Route
            path={RoutesList.MahsByScePage}
            element={
              <PrivateVesselRoute
                element={
                  isMahsByScePageEnabled ? (
                    <MahsPage />
                  ) : (
                    <Navigate to={RoutesList.NotFound} replace />
                  )
                }
              />
            }
          />
          <Route
            path={RoutesList.WonosPages}
            element={<PrivateVesselRoute element={<EpsRedirect page="Wo" />} />}
          />
          <Route
            path={RoutesList.WonosWithScePages}
            element={<PrivateVesselRoute element={<WonosPage />} />}
          />
          <Route
            path={RoutesList.TagsPage}
            element={<PrivateVesselRoute element={<EpsRedirect page="Tags" />} />}
          />
          <Route
            path={RoutesList.CaseToOperateDashboard}
            element={<PrivateVesselRoute element={<EpsRedirect page="DashBoard" />} />}
          />
          <Route
            path={RoutesList.CaseToOperate}
            element={<PrivateVesselRoute element={<EpsRedirect page="DashBoard" />} />}
          />
          <Route
            path={RoutesList.CaseToOperatePage}
            element={<PrivateVesselRoute element={<EpsRedirect page="CtoList" />} />}
          />
          <Route
            path={RoutesList.CaseToOperateFormPage}
            element={<PrivateVesselRoute element={<EpsRedirect page="CtoDetails" />} />}
          />
          {/* FACILITIES */}
          <Route
            path={RoutesList.FacilitiesPage}
            element={<PrivateFacilitiesRoute element={<EpsRedirect page="Facilities" />} />}
          />

          {/* HOME PAGE */}
          <Route
            path={RoutesList.HomePage}
            element={<PrivateRoute element={<HomePage />} validation={() => true} />}
          />

          <Route path={RoutesList.LoginPage} element={<Login />} />

          {/* SYSTEM */}

          <Route
            path={RoutesList.CountryRedirect}
            element={<Navigate to={RoutesList.HomePage} replace />}
          />
          <Route path="/redirectB2c" />
          <Route path={RoutesList.NotFound} element={<ErrorPage />} />
          <Route element={<Navigate to={RoutesList.NotFound} replace />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}
